import React, { useState, useEffect, useMemo } from 'react';
import { 
  Grid, 
  LinearProgress, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography, 
  Chip, 
  TablePagination,
  TextField,
  InputAdornment
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import {
  Page,
  Header,
  WarningPanel,
  CodeSnippet,
} from '@backstage/core-components';
import { Link } from 'react-router-dom';
import BreadcrumbsNav from '../common/BreadcrumbsNav/BreadcrumbsNav';
import BackButton from '../utils/backButton';
import { HelpComponent } from '../utils/helpComponent';
import { useCustomStyles } from '../leaderBoard/leaderBoardStyles';
import UserAvatar from '../common/UserAvatar';
import BadgeDisplayImage from '../leaderBoard/badgeTypePicker';
import {
  GamificationApi,
  gamificationApiRef,
} from '../../apis/gamificationApis';
import { useApi } from '@backstage/core-plugin-api';
import {
  NotificationApi,
  notificationApiRef,
} from '../../apis/notificationApi';
import { makeStyles } from '@material-ui/core/styles';
import SecurityIcon from '@material-ui/icons/Security';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';

interface CybersecurityLeaderboardEntry {
  email: string;
  displayname: string;
  score: number;
  levelname: string;
  levelid: string;
}

interface CybersecurityLevel {
  id: string;
  type: string;
  name: string;
  description: string;
  minpoints: number;
  maxpoints: number;
}

const cybersecurityLeaderboardHelpUrl = 'https://cbre.sharepoint.com/sites/intra-ProductSecurityDevSecOps/SitePages/Cybersecurity-Leaderboard-Criteria.aspx?TeamsCID=d26af3e0-129d-4d4f-aeee-1b033df6da9e&OR=Teams-HL&CT=1784050205288&xsdata=MDV8MDJ8fDI4M2YzNDg5NDRjZjRlZDU2ODE2MDhkZWUxY2RhZDI5fDAxNTllOWQwMDlhMDRlZGY5NmJhYTNkZWVhMzYzYzI4fDB8MHw2MzkxOTY0NzA2MjI5MTE4MzN8VW5rbm93bnxWR1ZoYlhOVFpXTjFjbWwwZVZObGNuWnBZMlY4ZXlKRFFTSTZJbFJsWVcxelgwRlVVRk5sY25acFkyVmZVMUJQVEU5R0lpd2lWaUk2SWpBdU1DNHdNREF3SWl3aVVDSTZJbGRwYmpNeUlpd2lRVTRpT2lKUGRHaGxjaUlzSWxkVUlqb3hNWDA9fDF8TDJOb1lYUnpMekU1T2pGaE0yUmhaR0ZqTFRJelpUZ3ROR016TUMxaVpUQTRMV015T1RJMFptTTFZV0V3WlY5a09XRmxPVFF4WWkweE1ETmpMVFJtTXprdE9XUmhaaTFqT1RjM05tUmpNV0kxWkdGQWRXNXhMbWRpYkM1emNHRmpaWE12YldWemMyRm5aWE12TVRjNE5EQTFNREkyTURFME5nPT18ZDAwNjJiNzNiMTI3NDY4NmM5OTMwOGRlZTFjZGFkMjh8NDU3ZDVjMzE5MmFkNDM5YmExNTgwNGE5YWY3ODcwZDA%3D&sdata=WTRHVGlDOUx5OERVenpOYk9VL2J6TmVLa0pMQk54bFlZSzk5N3N4NlBEaz0%3D&ovuser=0159e9d0-09a0-4edf-96ba-a3deea363c28%2CHarrison.Chen1%40cbre.com&clickparams=eyJBcHBOYW1lIjoiVGVhbXMtRGVza3RvcCIsIkFwcFZlcnNpb24iOiI1MC8yNjA2MTExODIxNiIsIkhhc0ZlZGVyYXRlZFVzZXIiOmZhbHNlfQ%3D%3D';

const useStyles = makeStyles((theme) => ({
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  headerIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  rankCell: {
    fontWeight: 'bold',
    fontSize: '1.2rem',
  },
  firstPlace: {
    backgroundColor: '#FFD700',
    color: '#000',
  },
  secondPlace: {
    backgroundColor: '#C0C0C0',
    color: '#000',
  },
  thirdPlace: {
    backgroundColor: '#CD7F32',
    color: '#fff',
  },
  scoreCell: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  levelChip: {
    fontWeight: 'bold',
  },
  bronzeLevel: {
    backgroundColor: '#CD7F32',
    color: '#fff',
  },
  silverLevel: {
    backgroundColor: '#C0C0C0',
    color: '#000',
  },
  goldLevel: {
    backgroundColor: '#FFD700',
    color: '#000',
  },
  tableHeader: {
    backgroundColor: theme.palette.grey[100],
  },
  statsContainer: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    padding: theme.spacing(2),
    textAlign: 'center',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  tabsContainer: {
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tabIcon: {
    marginRight: theme.spacing(1),
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
}));

export const CybersecurityLeaderboardPage = () => {
  const classes = useCustomStyles();
  const customStyles = useStyles();

  const [data, setData] = useState<CybersecurityLeaderboardEntry[]>([]);
  const [levels, setLevels] = useState<CybersecurityLevel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedFilter] = useState<string>('cumulative');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  const gamificationApi: GamificationApi = useApi(gamificationApiRef);
  const notificationApi: NotificationApi = useApi(notificationApiRef);

  const breadcrumbRoutes = [
    {
      path: '/',
      title: 'Home',
    },
    {
      path: '/leaderboard/cybersecurity',
      title: 'Cybersecurity Application Security Leaderboard',
    },
  ];

  const filters = [
    { key: 'cumulative', label: 'Cumulative' },
    { key: 'cm', label: 'Monthly' },
    { key: 'cq', label: 'Quarterly' },
    { key: 'cy', label: 'Yearly' },
  ];

  const fetchData = (filter?: string) => {
    setIsLoading(true);
    setPage(0); // Reset pagination when changing filters
    
    const filterToUse = filter || selectedFilter;
    
    gamificationApi.getCybersecurityLeaderBoardWithFilter(filterToUse)
      .then((res: any) => {
        // Since the API methods already return res.data, 'res' is the actual data array
        if (res && Array.isArray(res)) {
          setData(res);
        } else if (res && res.data && Array.isArray(res.data)) {
          // Fallback for nested response structure
          setData(res.data);
        } else {
          console.warn('Unexpected API response structure:', res);
          setData([]);
        }
        setIsLoading(false);
      })
      .catch((error: any) => {
        setIsLoading(false);
        notificationApi.sendNotification({
          message: `Error occurred - ${error?.message || 'Failed to fetch cybersecurity application security leaderboard'}`,
          disapperAfterMs: 2500,
          severity: 'error',
        });
      });
  };

  const fetchLevels = () => {
    gamificationApi.getCybersecurityLevels()
      .then((res: any) => {
        if (res && Array.isArray(res)) {
          setLevels(res);
        } else if (res && res.data && Array.isArray(res.data)) {
          setLevels(res.data);
        } else {
          console.warn('Unexpected levels API response structure:', res);
          setLevels([]);
        }
      })
      .catch((error: any) => {
        console.error('Error fetching cybersecurity levels:', error);
        setLevels([]);
      });
  };

  useEffect(() => {
    fetchData();
    fetchLevels();
  }, [gamificationApi, notificationApi]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getLevelDetails = (levelname: string) => {
    return levels.find(level => level.name === levelname);
  };

  const calculateActualLevel = (score: number) => {
    if (!levels || levels.length === 0) return null;
    
    // Sort levels by minpoints to ensure we check from lowest to highest
    const sortedLevels = [...levels].sort((a, b) => a.minpoints - b.minpoints);
    
    // Find the appropriate level based on score
    let currentLevel = sortedLevels[0]; // Default to first level
    
    for (const level of sortedLevels) {
      if (score >= level.minpoints && score <= level.maxpoints) {
        currentLevel = level;
        break;
      }
    }
    
    return currentLevel;
  };

  const calculateProgress = (score: number, levelname: string) => {
    const level = getLevelDetails(levelname);
    if (!level) return 0;
    
    const range = level.maxpoints - level.minpoints;
    const correctedStartValue = score - level.minpoints;
    const percentage = (correctedStartValue * 100) / range;
    
    return Math.min(Math.max(Math.round(percentage), 0), 100);
  };

  // Transform data to use calculated levels based on score
  const dataWithCalculatedLevels = useMemo(() => {
    return data.map(entry => {
      const actualLevel = calculateActualLevel(entry.score);
      return {
        ...entry,
        levelname: actualLevel?.name || entry.levelname,
        levelid: actualLevel?.id || entry.levelid
      };
    });
  }, [data, levels]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return dataWithCalculatedLevels;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return dataWithCalculatedLevels.filter(entry => {
      const displayName = (entry.displayname && entry.displayname.split('@')[0]) ||
                          (entry.email && entry.email.split('@')[0]) ||
                          '';
      const email = entry.email || '';
      
      return displayName.toLowerCase().includes(searchLower) ||
             email.toLowerCase().includes(searchLower);
    });
  }, [dataWithCalculatedLevels, searchTerm]);

  const totalParticipants = filteredData.length;

  // Memoized pagination calculations
  const paginatedData = useMemo(() => {
    return filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);
  
  const globalRankOffset = page * rowsPerPage;

  if (isLoading) {
    return (
      <Page themeId="home">
        <Header
          title="Cybersecurity Application Security Leaderboard"
          pageTitleOverride="Cybersecurity Application Security Leaderboard"
          subtitle={<BreadcrumbsNav lastRoutes={breadcrumbRoutes} skipLast={false} />}
        />
        <div className={classes.levelAndBadgesContainer}>
          <LinearProgress />
        </div>
      </Page>
    );
  }

  if (!isLoading && data.length === 0) {
    return (
      <Page themeId="home">
        <Header
          title="Cybersecurity Application Security Leaderboard"
          pageTitleOverride="Cybersecurity Application Security Leaderboard"
          subtitle={<BreadcrumbsNav lastRoutes={breadcrumbRoutes} skipLast={false} />}
        />
        <div>
          <WarningPanel
            severity="warning"
            title="No cybersecurity application security leaderboard data available"
          >
            <CodeSnippet language="text" text="No data found for Cycode leaderboard" />
          </WarningPanel>
        </div>
      </Page>
    );
  }

  return (
    <Page themeId="home">
      <Header
        title="Cybersecurity Application Security Leaderboard"
        pageTitleOverride="Cybersecurity Application Security Leaderboard"
        subtitle={<BreadcrumbsNav lastRoutes={breadcrumbRoutes} skipLast={false} />}
      />
      <div className={classes.levelAndBadgesContainer}>
        <Grid container direction="row" spacing={3}>
          <Grid item xs={12}>
            <BackButton />
            <HelpComponent helpUrl={cybersecurityLeaderboardHelpUrl} />
          </Grid>
        </Grid>
        <div className={customStyles.headerContainer}>
          <SecurityIcon className={customStyles.headerIcon} />
          <Typography variant="h4" component="h1">
            Application Security Champions
          </Typography>
          <Chip
            label={filters.find(f => f.key === selectedFilter)?.label || 'Cumulative'}
            color="primary"
            variant="outlined"
            style={{ marginLeft: '16px' }}
          />
        </div>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            {/* Statistics Cards */}
            <Grid container spacing={3} className={customStyles.statsContainer}>
              <Grid item xs={12} sm={6}>
                <div className={customStyles.statCard}>
                  <Typography variant="h6" color="textSecondary">
                    Total Participants
                  </Typography>
                  <Typography variant="h3" color="primary">
                    {totalParticipants}
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <div className={customStyles.statCard}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setPage(0); // Reset to first page when searching
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    style={{ marginTop: '8px' }}
                  />
                </div>
              </Grid>
            </Grid>

            {/* Leaderboard Table */}
            <TableContainer component={Paper} className={customStyles.tableContainer}>
              <Table>
                <TableHead className={customStyles.tableHeader}>
                  <TableRow>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <EmojiEventsIcon style={{ marginRight: '8px' }} />
                        Rank
                      </div>
                    </TableCell>
                    <TableCell>User</TableCell>
                    <TableCell align="center">Level & Progress</TableCell>
                    <TableCell align="right">Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((entry, index) => {
                    const globalRank = globalRankOffset + index + 1;
                    const displayName = 
                      (entry.displayname && entry.displayname.split('@')[0]) ||
                      (entry.email && entry.email.split('@')[0]) ||
                      'Unknown User';
                    
                    return (
                      <TableRow key={entry.email} hover>
                        <TableCell className={customStyles.rankCell}>
                          #{globalRank}
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <UserAvatar
                              userEmail={entry.email}
                              customStyles={{
                                width: '40px',
                                height: '40px',
                                fontSize: '16px',
                                fontWeight: 'normal',
                                marginRight: '12px',
                              }}
                            />
                            <div>
                              <Link
                                to={`/leaderboard/cybersecurity/${entry.email.split('@')[0]}`}
                                state={{ email: entry.email }}
                                style={{ textDecoration: 'none' }}
                              >
                                <Typography variant="body1" color="textSecondary" style={{ fontWeight: 500 }}>
                                  {displayName}
                                </Typography>
                              </Link>
                              <Typography variant="body2" color="textSecondary">
                                {entry.email}
                              </Typography>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell align="center">
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '200px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                              <BadgeDisplayImage type={entry.levelname} />
                              <Typography variant="body2" style={{ marginLeft: '8px', fontWeight: 500 }}>
                                {entry.levelname}
                              </Typography>
                            </div>
                            {levels.length > 0 && (
                              <div style={{ width: '100%' }}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={calculateProgress(entry.score, entry.levelname)} 
                                  style={{ height: '6px', borderRadius: '3px' }}
                                />
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '12px', color: 'inherit', opacity: 0.6 }}>
                                  <span>{getLevelDetails(entry.levelname)?.minpoints || 0}</span>
                                  <span>{entry.score} / {getLevelDetails(entry.levelname)?.maxpoints || 0}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell align="right" className={customStyles.scoreCell}>
                          {entry.score.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                }
              />
            </TableContainer>
          </Grid>
        </Grid>
      </div>
    </Page>
  );
};